import * as XLSX from 'xlsx'

/**
 * Converts grid data to XLSX format with all columns as text data type
 * @param {Array} data - Array of row objects
 * @param {Array} columnKeys - Array of column keys to include in export
 * @param {string} fileName - Name for the exported file (without extension)
 * @param {Function} processCellCallback - Optional callback to process cell values
 * @param {Object} columnDefinitions - Column definitions with readable names
 */
export const exportToXLSX = (data, columnKeys, fileName = 'export', processCellCallback, columnDefinitions) => {
  try {
    if (!data || !data.length) {
      return
    }

    if (!columnKeys || !columnKeys.length) {
      return
    }

    // Create a new workbook
    const workbook = XLSX.utils.book_new()

    // Create a mapping of column keys to readable names
    const columnKeyToReadable = {}
    if (columnDefinitions) {
      Object.values(columnDefinitions).forEach((colDef) => {
        if (colDef.columnKey && colDef.readable) {
          columnKeyToReadable[colDef.columnKey] = colDef.readable
        }
      })
    }

    // Prepare the data for export
    const exportData = data.map((row) => {
      const exportRow = {}

      columnKeys.forEach((colKey) => {
        let cellValue = row[colKey]

        // Apply processing callback if provided
        if (processCellCallback) {
          const processedValue = processCellCallback({
            value: cellValue,
            column: { colId: colKey },
            data: row,
          })
          cellValue = processedValue
        }

        // Use readable name as header if available, otherwise use column key
        const headerName = columnKeyToReadable[colKey] || colKey

        // Handle null/undefined values
        if (cellValue === null || cellValue === undefined) {
          exportRow[headerName] = ''
        } else {
          // Special handling for fee column - keep as number for decimal formatting
          if (colKey === 'fee') {
            // Parse as number and ensure it's a valid number
            const numericValue = parseFloat(cellValue)
            if (!isNaN(numericValue)) {
              // Round to 2 decimal places
              exportRow[headerName] = Math.round(numericValue * 100) / 100
            } else {
              // If not a valid number, store as empty string
              exportRow[headerName] = ''
            }
          } else {
            // Convert all other values to strings to ensure text data type
            exportRow[headerName] = String(cellValue)
          }
        }
      })

      return exportRow
    })

    // Create worksheet from the data
    const worksheet = XLSX.utils.json_to_sheet(exportData)

    // Set cell formats - text for most columns, number for fee column
    const range = XLSX.utils.decode_range(worksheet['!ref'])

    // Get the header row to identify which column is the fee column
    const headerRow = worksheet['!ref'] ? XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] : []
    const feeColumnIndex = headerRow.findIndex(header =>
      header === (columnKeyToReadable.fee || 'fee')
    )

    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col })
        const cell = worksheet[cellAddress]

        if (cell) {
          // Special handling for fee column (except header row)
          if (col === feeColumnIndex && row > range.s.r) {
            // Set cell type to number for fee column data rows
            if (typeof cell.v === 'number') {
              cell.t = 'n' // 'n' indicates number type
              cell.z = '0.00' // Number format with 2 decimal places
            } else {
              // If not a number, treat as text
              cell.t = 's'
              cell.v = String(cell.v || '')
            }
          } else {
            // Set cell type to text for all other columns
            cell.t = 's' // 's' indicates string/text type
            if (cell.v !== null && cell.v !== undefined) {
              cell.v = String(cell.v)
            }
          }
        }
      }
    }

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

    // Generate the file and trigger download
    const xlsxFileName = `${fileName}.xlsx`
    XLSX.writeFile(workbook, xlsxFileName)
  } catch (error) {
    console.error('XLSX Export Error:', error)
    throw error
  }
}

/**
 * Utility function to get column headers from column definitions
 * @param {Array} columnDefs - Array of column definitions
 * @param {Array} columnKeys - Array of column keys to include
 * @returns {Object} Map of column keys to header names
 */
export const getColumnHeaders = (columnDefs, columnKeys) => {
  const headerMap = {}

  if (!columnDefs || !columnKeys) {
    return headerMap
  }

  columnDefs.forEach((colDef) => {
    if (columnKeys.includes(colDef.field || colDef.colId)) {
      const key = colDef.field || colDef.colId
      headerMap[key] = colDef.headerName || colDef.field || key
    }
  })

  return headerMap
}
